// Customer-facing quote view at /p/quote/:token. Applies the org's saved
// template branding. Includes an Accept Quote action (POSTs to the
// dodobooks_accept_quote RPC) with a typed-name signature.

import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { Printer, Check, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { formatDate, formatMoney } from '../lib/format';
import type { Quote, QuoteLine, Organization, Contact, TaxCode, InvoiceTemplate, InvoiceTemplateSettings } from '../lib/types';

const DEFAULTS: InvoiceTemplateSettings = {
  primary_color: '#0c8f60', accent_color: '#d6a85a',
  paper_color: '#f5efe0', text_color: '#0f1521',
  font_family: 'Fraunces', title_quote: 'Quote',
  header_layout: 'logo-left', show_vat_column: true,
};

export default function QuotePublic() {
  const { token } = useParams();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [lines, setLines] = useState<QuoteLine[]>([]);
  const [org, setOrg] = useState<Organization | null>(null);
  const [contact, setContact] = useState<Contact | null>(null);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [tpl, setTpl] = useState<InvoiceTemplateSettings>(DEFAULTS);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [showAccept, setShowAccept] = useState(false);
  const [acceptName, setAcceptName] = useState('');
  const [acceptedMsg, setAcceptedMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      const { data: q } = await supabase.from('dodobooks_quotes').select('*').eq('public_token', token).maybeSingle();
      if (!q) { setErr('Quote not found. Check the link and try again.'); setLoading(false); return; }
      setQuote(q as Quote);
      const [ls, o, c, tcs, t] = await Promise.all([
        supabase.from('dodobooks_quote_lines').select('*').eq('quote_id', (q as any).id).order('position'),
        supabase.from('dodobooks_organizations').select('*').eq('id', (q as any).org_id).maybeSingle(),
        supabase.from('dodobooks_contacts').select('*').eq('id', (q as any).contact_id).maybeSingle(),
        supabase.from('dodobooks_tax_codes').select('*').eq('org_id', (q as any).org_id),
        supabase.from('dodobooks_invoice_templates').select('settings').eq('org_id', (q as any).org_id).eq('is_default', true).maybeSingle(),
      ]);
      setLines((ls.data ?? []) as QuoteLine[]);
      setOrg(o.data as Organization);
      setContact(c.data as Contact);
      setTaxCodes((tcs.data ?? []) as TaxCode[]);
      if (t.data?.settings) setTpl({ ...DEFAULTS, ...(t.data.settings as InvoiceTemplateSettings) });
      setLoading(false);
    })();
  }, [token]);

  async function accept(e: FormEvent) {
    e.preventDefault();
    if (!acceptName.trim() || !token) return;
    setAccepting(true); setErr(null);
    const { data, error } = await supabase.rpc('dodobooks_accept_quote', {
      p_token: token,
      p_name: acceptName.trim(),
      p_signature: null,
    });
    setAccepting(false);
    if (error) { setErr(error.message); return; }
    if (data?.ok === false) { setErr(data.error ?? 'Could not accept'); return; }
    setAcceptedMsg('Thank you — your acceptance has been recorded.');
    setShowAccept(false);
    // Re-fetch the quote so the page shows "Accepted"
    if (quote) {
      const { data: fresh } = await supabase.from('dodobooks_quotes').select('*').eq('id', quote.id).maybeSingle();
      if (fresh) setQuote(fresh as Quote);
    }
  }

  if (loading) return <div className="min-h-screen grid place-items-center text-text-mute">Loading quote…</div>;
  if (err && !quote) return (
    <div className="min-h-screen grid place-items-center px-6">
      <div className="text-center max-w-md">
        <div className="text-rust text-sm mb-4">{err}</div>
      </div>
    </div>
  );
  if (!quote || !org || !contact) return null;

  const primary = tpl.primary_color ?? '#0c8f60';
  const accent = tpl.accent_color ?? '#d6a85a';
  const paper = tpl.paper_color ?? '#f5efe0';
  const fontFamily = tpl.font_family ?? 'Fraunces';
  const title = tpl.title_quote ?? 'Quote';
  const isExpired = quote.status === 'expired' || (quote.status === 'sent' && new Date(quote.expiry_date) < new Date());
  const isAccepted = quote.status === 'accepted' || quote.status === 'converted';
  const isDeclined = quote.status === 'declined';
  const canAccept = quote.status === 'sent' && !isExpired;

  return (
    <div className="min-h-screen bg-ink text-text">
      <header className="border-b border-line bg-ink-2 print:hidden">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-xs font-mono text-text-mute uppercase tracking-widest">
            <img src="/logo.svg" alt="" width={20} height={20} className="rounded" />
            <span>Powered by DodoBooks</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => window.print()} className="btn btn-secondary btn-sm"><Printer size={12} /> Print / Save PDF</button>
            {canAccept && (<button onClick={() => setShowAccept(true)} className="btn btn-primary btn-sm"><Check size={12} /> Accept quote</button>)}
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-6 print:p-0">
        <article className="rounded-xl shadow-2xl print:shadow-none print:rounded-none print:bg-white relative overflow-hidden" style={{ background: paper, color: tpl.text_color ?? '#0f1521' }}>
          {/* Status stamp */}
          {(isAccepted || isExpired || isDeclined) && (
            <div className="absolute top-8 right-8 px-4 py-2 italic font-bold text-2xl tracking-wider border-4 rounded-md rotate-[-8deg]"
              style={{ fontFamily, borderColor: isAccepted ? primary : '#d97757', color: isAccepted ? primary : '#d97757', background: 'rgba(255,255,255,0.3)' }}>
              {isAccepted ? 'ACCEPTED' : isDeclined ? 'DECLINED' : 'EXPIRED'}
            </div>
          )}

          {tpl.show_banner && tpl.banner_url && (<img src={tpl.banner_url} alt="" className="w-full h-24 object-cover" />)}

          <div className="p-10 print:p-8">
            <div className={'mb-8 ' + (tpl.header_layout === 'logo-center' ? 'text-center' : 'grid grid-cols-2 gap-6')}>
              <div className={tpl.header_layout === 'logo-right' ? 'order-2 text-right' : ''}>
                {tpl.logo_url
                  ? <img src={tpl.logo_url} alt={org.name} className={'h-14 object-contain mb-3 ' + (tpl.header_layout === 'logo-center' ? 'mx-auto' : '')} />
                  : <div className="font-bold text-2xl mb-2" style={{ fontFamily }}>{org.name}</div>}
                <div className="text-xs leading-relaxed" style={{ color: 'rgba(15,21,33,0.7)' }}>
                  {org.legal_name && org.legal_name !== org.name && <div>{org.legal_name}</div>}
                  {org.address && <div>{org.address}</div>}
                  {(org.city || org.province) && <div>{[org.city, org.province, org.postal_code].filter(Boolean).join(', ')}</div>}
                  {org.phone && <div>{org.phone}</div>}
                  {org.email && <div>{org.email}</div>}
                  {org.vat_number && <div className="font-mono mt-1">VAT {org.vat_number}</div>}
                </div>
              </div>
              <div className={tpl.header_layout === 'logo-right' ? 'order-1 text-left' : tpl.header_layout === 'logo-center' ? 'mt-6' : 'text-right'}>
                <div className="font-bold text-4xl tracking-tight" style={{ fontFamily }}>{title}</div>
                <div className="font-mono text-sm" style={{ color: 'rgba(15,21,33,0.65)' }}>#{quote.number}</div>
                <div className="mt-6 inline-block text-left">
                  <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>For</div>
                  <div className="font-semibold mt-0.5">{contact.name}</div>
                  {contact.email && <div className="text-xs">{contact.email}</div>}
                </div>
              </div>
            </div>

            {tpl.greeting_text && <div className="text-sm mb-4 italic" style={{ color: 'rgba(15,21,33,0.75)' }}>{tpl.greeting_text}</div>}
            {quote.notes && <div className="text-sm mb-4" style={{ color: 'rgba(15,21,33,0.75)' }}>{quote.notes}</div>}

            <div className="grid grid-cols-3 gap-4 mb-6 py-3 border-y" style={{ borderColor: 'rgba(15,21,33,0.15)' }}>
              <div><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Issued</div><div className="font-semibold text-sm mt-0.5">{formatDate(quote.issue_date)}</div></div>
              <div><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Valid until</div><div className={'font-semibold text-sm mt-0.5 ' + (isExpired ? 'text-[#d97757]' : '')}>{formatDate(quote.expiry_date)}</div></div>
              <div className="text-right"><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Reference</div><div className="font-semibold text-sm mt-0.5 font-mono">{quote.reference || quote.number}</div></div>
            </div>

            <table className="w-full mb-6">
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(15,21,33,0.18)' }}>
                  <th className="text-left py-2 text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Description</th>
                  <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-14" style={{ color: 'rgba(15,21,33,0.55)' }}>Qty</th>
                  <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-24" style={{ color: 'rgba(15,21,33,0.55)' }}>Unit</th>
                  {tpl.show_vat_column !== false && <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-14" style={{ color: 'rgba(15,21,33,0.55)' }}>VAT</th>}
                  <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-24" style={{ color: 'rgba(15,21,33,0.55)' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const tc = taxCodes.find((t) => t.id === l.tax_code_id);
                  return (
                    <tr key={l.id} style={{ borderBottom: '1px dashed rgba(15,21,33,0.1)' }}>
                      <td className="py-3 align-top text-sm">{l.description}</td>
                      <td className="py-3 text-right font-mono text-sm">{Number(l.qty).toLocaleString('en-ZA')}</td>
                      <td className="py-3 text-right font-mono text-sm">{formatMoney(l.unit_price, quote.currency)}</td>
                      {tpl.show_vat_column !== false && <td className="py-3 text-right font-mono text-xs" style={{ color: 'rgba(15,21,33,0.65)' }}>{tc ? `${(tc.rate * 100).toFixed(0)}%` : '—'}</td>}
                      <td className="py-3 text-right font-mono text-sm font-semibold">{formatMoney(l.line_total, quote.currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="flex justify-end mb-4">
              <div className="w-64 text-sm">
                <div className="flex justify-between py-1"><span style={{ color: 'rgba(15,21,33,0.65)' }}>Subtotal</span><span className="font-mono">{formatMoney(quote.subtotal, quote.currency)}</span></div>
                <div className="flex justify-between py-1 pb-2" style={{ borderBottom: '1px solid rgba(15,21,33,0.15)' }}><span style={{ color: 'rgba(15,21,33,0.65)' }}>VAT</span><span className="font-mono">{formatMoney(quote.tax_total, quote.currency)}</span></div>
                <div className="flex justify-between py-2 font-bold text-2xl" style={{ fontFamily, borderBottom: `2px solid ${primary}` }}>
                  <span>Total</span><span className="font-mono" style={{ color: primary }}>{formatMoney(quote.total, quote.currency)}</span>
                </div>
              </div>
            </div>

            {acceptedMsg && (
              <div className="mt-4 rounded-lg p-4 text-center font-semibold" style={{ background: `${primary}15`, color: primary, border: `1px solid ${primary}50` }}>
                {acceptedMsg}
              </div>
            )}
            {isAccepted && quote.accepted_by_name && (
              <div className="mt-4 rounded-lg p-3 text-sm" style={{ background: `${primary}10`, border: `1px solid ${primary}30` }}>
                <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Accepted</div>
                <div className="mt-1"><strong>{quote.accepted_by_name}</strong> · {quote.accepted_at ? formatDate(quote.accepted_at) : ''}</div>
              </div>
            )}

            {(quote.terms || tpl.terms_text) && (
              <div className="mt-6 pt-3 text-xs" style={{ color: 'rgba(15,21,33,0.7)', borderTop: '1px solid rgba(15,21,33,0.12)' }}>
                <div className="text-[10px] uppercase tracking-widest font-mono mb-1" style={{ color: 'rgba(15,21,33,0.55)' }}>Terms</div>
                {quote.terms || tpl.terms_text}
              </div>
            )}

            {tpl.footer_text && (
              <div className="mt-4 pt-3 text-center text-xs italic" style={{ color: 'rgba(15,21,33,0.55)', borderTop: '1px solid rgba(15,21,33,0.08)' }}>
                {tpl.footer_text}
              </div>
            )}

            <div className="mt-4 h-1 rounded" style={{ background: `linear-gradient(90deg, ${primary} 0%, ${accent} 100%)` }} />
          </div>
        </article>
      </div>

      {showAccept && (
        <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm grid place-items-center px-4">
          <form onSubmit={accept} className="bg-surface border border-line-strong rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-line">
              <h2 className="h-section">Accept quote {quote.number}</h2>
              <button type="button" onClick={() => setShowAccept(false)} className="text-text-mute hover:text-text"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-text-dim">
                By accepting this quote you confirm the {formatMoney(quote.total, quote.currency)} total and agree to {org.legal_name || org.name}'s terms.
              </p>
              <div className="field">
                <label>Your full name *</label>
                <input required autoFocus value={acceptName} onChange={(e) => setAcceptName(e.target.value)} placeholder="Type your name to sign" />
                <p className="text-[11px] text-text-mute mt-1">Acts as your electronic signature.</p>
              </div>
              {err && <div className="text-sm text-rust bg-rust/10 border border-rust/30 px-3 py-2 rounded-md">{err}</div>}
              <div className="flex justify-end gap-2 pt-2 border-t border-line">
                <button type="button" onClick={() => setShowAccept(false)} className="btn btn-ghost">Cancel</button>
                <button type="submit" disabled={accepting || !acceptName.trim()} className="btn btn-primary disabled:opacity-50">
                  <Check size={14} /> {accepting ? 'Accepting…' : 'Accept quote'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      <style>{`@media print { @page { size: ${tpl.page_size === 'Letter' ? 'Letter' : 'A4'}; margin: 12mm; } body { background: white; } }`}</style>
    </div>
  );
}
